import React, { useState, useEffect } from 'react';
import './ManufacturerManager.css';

function ManufacturerManager() {
  const [manufacturers, setManufacturers] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logo: '',
    website: '',
    productTypeIds: []
  });

  useEffect(() => {
    fetchManufacturers();
    fetchProductTypes();
  }, []);

  const fetchManufacturers = async () => {
    try {
      const res = await fetch('/api/manufacturers');
      const data = await res.json();
      setManufacturers(Array.isArray(data) ? data : []);
      setError('');
    } catch (err) {
      setError('Fehler beim Laden der Hersteller');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductTypes = async () => {
    try {
      const res = await fetch('/api/product-types');
      const data = await res.json();
      setProductTypes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Fehler beim Laden der Produkttypen:', err);
    }
  };

  const handleAddClick = () => {
    setEditingId(null);
    setFormData({
      name: '',
      description: '',
      logo: '',
      website: '',
      productTypeIds: []
    });
    setShowForm(true);
  };

  const handleEditClick = (manufacturer) => {
    setEditingId(manufacturer._id);
    setFormData({
      name: manufacturer.name || '',
      description: manufacturer.description || '',
      logo: manufacturer.logo || '',
      website: manufacturer.website || '',
      productTypeIds: manufacturer.productTypeIds || []
    });
    setShowForm(true);
  };

  const toggleProductType = (productTypeId) => {
    setFormData(prev => ({
      ...prev,
      productTypeIds: prev.productTypeIds.includes(productTypeId)
        ? prev.productTypeIds.filter(id => id !== productTypeId)
        : [...prev.productTypeIds, productTypeId]
    }));
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getProductTypeNameById = (id) => {
    const type = productTypes.find(t => t._id === id);
    return type?.name || '';
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Name ist erforderlich');
      return;
    }

    try {
      const url = editingId
        ? `/api/manufacturers/${editingId}`
        : '/api/manufacturers';
      
      const method = editingId ? 'PATCH' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        fetchManufacturers();
        setShowForm(false);
        setEditingId(null);
        alert(editingId ? 'Hersteller aktualisiert' : 'Hersteller hinzugefügt');
      } else {
        setError('Fehler beim Speichern');
      }
    } catch (err) {
      setError('Fehler: ' + err.message);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`"${name}" wirklich löschen?`)) {
      return;
    }

    try {
      const res = await fetch(`/api/manufacturers/${id}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        fetchManufacturers();
        alert('Hersteller gelöscht');
      } else {
        setError('Fehler beim Löschen');
      }
    } catch (err) {
      setError('Fehler: ' + err.message);
    }
  };

  if (loading) return <div className="manufacturer-manager"><p>Lädt...</p></div>;

  return (
    <div className="manufacturer-manager">
      <div className="manufacturer-header">
        <h2>🏭 Hersteller Manager</h2>
        {!showForm && (
          <button className="btn-add-manufacturer" onClick={handleAddClick}>
            ➕ Neuer Hersteller
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <form className="manufacturer-form" onSubmit={handleFormSubmit}>
          <div className="form-title">
            {editingId ? '✏️ Hersteller Bearbeiten' : '➕ Neuer Hersteller'}
          </div>
          
          <div className="form-grid">
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                placeholder="z.B. Sanlight"
                required
              />
            </div>

            <div className="form-group">
              <label>Beschreibung</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                placeholder="Herstellerbeschreibung..."
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>Website</label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleFormChange}
                placeholder="https://..."
              />
            </div>

            <div className="form-group">
              <label>Logo URL</label>
              <input
                type="url"
                name="logo"
                value={formData.logo}
                onChange={handleFormChange}
                placeholder="https://..."
              />
              {formData.logo && (
                <img src={formData.logo} alt="Logo Preview" className="logo-preview" />
              )}
            </div>

            <div className="form-group">
              <label>Produkttypen</label>
              <div className="product-types-checkboxes">
                {productTypes.map(type => (
                  <label key={type._id} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.productTypeIds.includes(type._id)}
                      onChange={() => toggleProductType(type._id)}
                    />
                    <span>{type.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-save">💾 Speichern</button>
            <button
              type="button"
              className="btn-cancel"
              onClick={() => {
                setShowForm(false);
                setEditingId(null);
              }}
            >
              ❌ Abbrechen
            </button>
          </div>
        </form>
      )}

      <div className="manufacturers-grid">
        {manufacturers.map(manufacturer => (
          <div key={manufacturer._id} className="manufacturer-card">
            <div className="manufacturer-card-header">
              {manufacturer.logo && (
                <img src={manufacturer.logo} alt={manufacturer.name} className="manufacturer-card-logo" />
              )}
              <h3>{manufacturer.name}</h3>
            </div>
            
            <div className="manufacturer-card-body">
              {manufacturer.description && (
                <p className="description">{manufacturer.description}</p>
              )}
              {manufacturer.website && (
                <p><a href={manufacturer.website} target="_blank" rel="noopener noreferrer">🔗 Website</a></p>
              )}
              {manufacturer.productTypeIds && manufacturer.productTypeIds.length > 0 && (
                <div className="product-types-tags">
                  <strong>Produkttypen:</strong>
                  {manufacturer.productTypeIds.map(id => {
                    const typeName = getProductTypeNameById(id);
                    return typeName ? (
                      <span key={id} className="product-type-tag">{typeName}</span>
                    ) : null;
                  })}
                </div>
              )}
            </div>

            <div className="manufacturer-card-footer">
              <button
                className="btn-edit"
                onClick={() => handleEditClick(manufacturer)}
                title="Bearbeiten"
              >
                ✏️ Bearbeiten
              </button>
              <button
                className="btn-delete"
                onClick={() => handleDelete(manufacturer._id, manufacturer.name)}
                title="Löschen"
              >
                🗑️ Löschen
              </button>
            </div>
          </div>
        ))}
      </div>

      {manufacturers.length === 0 && !showForm && (
        <p className="no-manufacturers">📭 Keine Hersteller vorhanden</p>
      )}
    </div>
  );
}

export default ManufacturerManager;
